import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { skills as defaultSkills } from "../../../shared/skills";
import { config } from "../../environment";
import { useDefaultSkillGrants } from "../../hooks/useDefaultSkillGrants";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Button } from "../Button";
import { useModalId } from "../globalModalStack";
import { SkillOption } from "./SkillOption";
import { SkillsLayout } from "./SkillsLayout";

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, 216px);
  gap: 12px;
  padding: 24px;
`;

export function Browse() {
  const z = useZero();
  const canCreate = useHasAccess("tenant:skill_create");
  const grants = useDefaultSkillGrants();
  const { q } = useCurrentUrl(routes.skills.browse);
  const [createdSkillId, setCreatedSkillId] = useState<string | null>(null);
  const [skill] = useQuery(
    queries.loadSkill({ skillId: createdSkillId ?? "" }),
    { enabled: !!createdSkillId }
  );

  const filtered = useMemo(() => {
    if (!q?.trim()) return defaultSkills;
    const lower = q.toLowerCase();
    return defaultSkills.filter(
      (s) =>
        s.name.toLowerCase().includes(lower) ||
        s.description.toLowerCase().includes(lower)
    );
  }, [q]);

  const skillModalOpen = useModalId("skill");

  useEffect(() => {
    if (createdSkillId && !skillModalOpen) {
      const justCreated = skill && !skill.fresh ? skill.id : "";
      navigate(routes.skills.browse, { justCreated }, { replace: true });
      setCreatedSkillId(null);
    }
  }, [createdSkillId, skillModalOpen, skill]);

  const create = (template?: {
    name: string;
    description: string;
    instructions: string;
  }) => {
    if (!canCreate) return;
    const newId = nanoid(12);
    z.mutate(
      sharedMutators.createSkill({
        skillId: newId,
        name: template?.name ?? "",
        description: template?.description ?? "",
        instructions: template?.instructions ?? "",
        enabled: true,
        grants,
      })
    );
    setCreatedSkillId(newId);
    navigate(routes.modal.skill, { id: newId });
  };

  return (
    <>
      <title>{`Browse templates • ${config.title}`}</title>
      <SkillsLayout
        searchAction={
          canCreate ? (
            <Button size="l" icon="plus" onClick={() => create()} />
          ) : undefined
        }
      >
        <Grid>
          <SkillOption
            name="Blank skill"
            description="Start from scratch with your own instructions."
            icon="plus"
            disabled={!canCreate}
            onSelect={() => create()}
          />
          {filtered.map((s) => (
            <SkillOption
              key={s.name}
              name={s.name}
              description={s.description}
              disabled={!canCreate}
              onSelect={() =>
                create({
                  name: s.name,
                  description: s.description,
                  instructions: s.reference,
                })
              }
            />
          ))}
        </Grid>
      </SkillsLayout>
    </>
  );
}
